export interface LogItem {
    id?: string | number;
    avaTaxAccountId?: string;
    message?: string;
    createdAt?: string;
    jobId?: string;
}

export interface AccountListsState {
    data: {
        avaTaxAccountIds?: number[];
    };
    isLoading: boolean;
    error: string | null;
}

export interface JobLogsState {
    data: Record<string, unknown>;
    isLoading: boolean;
    error: string | null;
}

export interface EnableIamDsBulkAccountDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export interface BulkEnablementResult {
    ids: string[];
}

export interface Job {
    id: number;
    avaTaxAccountId: number;
    status: string;
    jobExternalId: string;
}

export interface Paginate {
    pageNumber: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
    totalRecords: number;
}

export interface JobState {
    data: Job[];
    isLoading: boolean;
    error: string | null;
    paginate: Paginate;
    statusFilter: string;
}

export interface JobStatusState {
    isLoading: boolean;
    error: string | null;
}

export interface FetchOldJobsDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export interface SessionState {
    rowsPerPage: number;
}

export interface PaginationEventDetail {
    rowsPerPage: number;
    startIndex: number;
    endIndex: number;
    currentPage: number;
    flag?: string;
}

export interface filter {
    jobType?: string;
    limit?: number;
    externalJobId?: string;
    jobIds?: number[];
}

/**
 * Determines the tag type for a log message based on its content
 * @param {string} message - The log message to analyze
 * @returns {string|null} - Returns tag type
 */
export const getMessageTagType = (message: string): string | null => {
    if (message.includes("Failed: ") || message.includes("ERROR: ")) {
        return "red-lighter";
    }
    if (message.includes("Warning: ") || message.includes("WARNING: ")) {
        return "yellow-light";
    }
    if (
        message.includes("Successfully processed") ||
        message.includes("ACCOUNT MIGRATION COMPLETED SUCCESSFULLY")
    ) {
        return "green-dark";
    }
    if (message.includes("Processing user:") || message.includes("STARTING ACCOUNT MIGRATION")) {
        return "green-light";
    }
    return null;
};

/**
 * Determines the tag type for a log message based on its content
 * @param {string} status - The status to analyze
 * @returns {string|null} - Returns tag type
 */
export const getStatusTagType = (status: string): string | null => {
    if (status === "F") {
        return "red-lighter";
    }
    if (status === "C") {
        return "green-dark";
    }
    if (status === "R") {
        return "green-light";
    }
    if (status === "Q") {
        return "yellow-light";
    }
    return null;
};

export const getStatusLabel = (status: string): string => {
    if (status === "F") {
        return "Failed";
    }
    if (status === "C") {
        return "Completed";
    }
    if (status === "R") {
        return "Running";
    }
    if (status === "Q") {
        return "Queued";
    }
    return "";
};

/**
 * Helper function to validate and normalize job items from API responses
 * @param rawItems - Raw job items from API
 * @returns Validated job items with consistent structure
 */
export const validateJobItems = (rawItems: Record<string, unknown>[] = []): Job[] => {
    return rawItems.map(item => ({
        id: (item.id as number) || 0,
        avaTaxAccountId: (item.avaTaxAccountId as number) || 0,
        status: (item.status as string) || "",
        jobExternalId: (item.externalJobId as string) || "",
    }));
};

/**
 * Helper function to update jobs state and reset pagination
 * @param state - Redux state object
 * @param payload - Raw job items from API
 */
export const updateJobsWithPagination = (
    state: {
        jobIds: { ids: number[] };
        jobs: JobState;
    },
    payload: Record<string, unknown>[]
): void => {
    const validatedItems = validateJobItems(payload);
    state.jobIds.ids = validatedItems.map(item => item.id);
    state.jobs.data = validatedItems;
    state.jobs.error = null;
    // Update pagination totalRecords and reset to first page
    state.jobs.paginate.totalRecords = validatedItems.length;
    state.jobs.paginate.pageNumber = 0;
    state.jobs.paginate.startIndex = 0;
    state.jobs.paginate.endIndex = Math.min(
        state.jobs.paginate.rowsPerPage - 1,
        validatedItems.length - 1
    );
};
